/**
 * Thrown when a create/save action is attempted while signed out.
 *
 * The sign-in prompt has already been shown by the time this is thrown, so the
 * UI should treat it as a cancelled action rather than a failure — no error
 * toast, no "operation failed" state. Panels check `isSignInRequired(error)`
 * before rendering an error.
 */
export class SignInRequiredError extends Error {
  constructor() {
    super('Sign in to create or save work.');
    this.name = 'SignInRequiredError';
  }
}

export function isSignInRequired(error: unknown): boolean {
  return error instanceof SignInRequiredError || (error as Error | undefined)?.name === 'SignInRequiredError';
}

/**
 * The message a submit handler should show for a caught error — none when the
 * error only means the user was just asked to sign in, or to top up.
 *
 * Both are prompts the user is already looking at, not failures. Rendering an
 * error underneath either one reads as "it broke" when the action is simply
 * waiting on them.
 */
export function submitErrorMessage(error: unknown, fallback = 'Invalid input.'): string | null {
  if (isSignInRequired(error)) return null;
  if ((error as Error | undefined)?.name === 'CreditsRequiredError') return null;
  return error instanceof Error ? error.message : fallback;
}
